<script lang="ts">
import { defineComponent, PropType, inject, ref, computed, watch, onMounted, nextTick, toRef } from 'vue'
import InputDefault from '@/components/form/InputDefault.vue'
import { formDefaultProps, formPartial } from '@/models/formPartial'
import { isValidMailDomain } from '@/utilities/validator'

export default defineComponent({
  name: 'InputFormMailAddress',

  components: {
    InputDefault,
  },

  props: {
    ...formDefaultProps,

    modelValue: {
      type: String,
      default: '',
    },

    fieldId: {
      type: String,
      default: ''
    },

    placeholder: {
      type: String,
      default: '',
    },

    /**
     * autocomplete 属性に渡す値
     * @see https://developer.mozilla.org/ja/docs/Web/HTML/Attributes/autocomplete
     */
    autocomplete: {
      type: String as PropType<AutocompleteAttr>,
      default: 'email',
    },

    /**
     * フォームのフォーカスアウト時に前後ホワイトスペースの trim するかどうか
     */
    hasTrim: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['update:modelValue', 'error', 'check:domain'],

  setup(props, { emit }) {
    // constants
    const INVALID_MAIL_DOMAINS = inject<Array<string>>('INVALID_MAIL_DOMAINS') || []

    // model
    const { hasError, isRequired, createErrorMessage } = formPartial(props.name, toRef(props, 'validationScheme'))

    const localValue = ref('')

    const computedHeader = computed((): string => {
      return props.header || '登録メールアドレス'
    })

    /**
     * フリーメールドメインかどうか
     */
    const isFreeMainDomain = computed(() => {
      return !isValidMailDomain(INVALID_MAIL_DOMAINS, localValue.value.toString())
    })

    watch(() => props.modelValue, () => {
      localValue.value = props.modelValue
    })

    onMounted(() => {
      localValue.value = props.modelValue
    })

    const onUpdate = async (value: string) => {
      emit('update:modelValue', value)

      // 親への更新データのバインド完了を待機した後にメールドメインチェック結果を emit する
      await nextTick()
      emit('check:domain', isFreeMainDomain.value)
    }

    return {
      // data
      localValue,

      // computed
      hasError,
      isRequired,
      computedHeader,
      isFreeMainDomain,

      // methods
      onUpdate,
      createErrorMessage,
    }
  }
})
</script>

<template>
  <dl class="dl">
    <dt
      class="dt u-text__bold u-text__small"
      data-tooltip="勤務先メールアドレスでのご登録で全機能をご利用いただけます"
      :data-free-domain="isFreeMainDomain">
      {{ computedHeader }}
      <span
        v-if="isRequired"
        class="span u-text__required">
        必須
      </span>
    </dt>
    <dd class="dd">
      <ValidationProvider
        :value="localValue"
        :namespace="namespace"
        :field="name"
        :scheme="validationScheme"
        :messages="createErrorMessage(false, false, computedHeader)"
        @error="$emit('error', $event)">
        <InputDefault
          :id="fieldId ? fieldId : undefined"
          :model-value="localValue"
          type="email"
          :name="name"
          :placeholder="placeholder || '例：bizhint@bizhint.jp'"
          :autocomplete="autocomplete"
          :has-trim="hasTrim"
          :is-disabled="isDisabled"
          :readonly="readonly"
          :is-error="hasError"
          @update:model-value="onUpdate($event)" />
      </ValidationProvider>

      <slot />
    </dd>
  </dl>
</template>

<style lang="sass" scoped>
.dl
  display: flex
  flex-direction: column
  align-items: flex-start
  container-type: inline-size

.dt
  +container(400px, $is-breakpoint-down: false)
    position: relative

    $bg-color: rgba($bg-warning, 1)
    &::before,
    &::after
      display: block
      position: absolute
      top: 50%
      font-size: $font-xx-small
      visibility: hidden
      opacity: 0
      transition: opacity 0.5s ease, right 0.3s ease, visibility 0.5s ease
      z-index: 1

    $triangle-width: 0.4em
    $triangle-height: 0.8em
    $tip-margin: 0.3em
    &::before
      content: ''
      width: $triangle-width
      height: $triangle-height
      right: 0
      transform: translateY(-50%)
      clip-path: polygon(0 50%, 100% 0, 100% 100%)
      background-color: $bg-color

    $font-size-px: $font-xx-small-px
    $font-size: math.div($font-size-px, 10)
    $gap: $font-size * 0.6
    $tip-height: $font-size + $gap * 2
    &::after
      content: attr(data-tooltip)
      right: 0
      transform: translateY(-50%) translateX(100%)
      overflow: hidden
      line-height: 1
      color: $font-orange
      font-weight: bold
      padding: #{$gap}em 1em
      background-color: $bg-color
      border-radius: 5px
      white-space: nowrap
      box-sizing: border-box

    &[data-free-domain="true"]
      &::before,
      &::after
        visibility: visible
        opacity: 1

      &::before
        right: -($tip-margin + $triangle-width)

      &::after
        right: -($tip-margin + $triangle-width)

.dd
  width: 100%
</style>
